import { useState } from "react";
import { commentsUpdateComment, commentsDeleteComment, commentsListComments } from "../../gen";
import type { CommentResponse, RuleResponse, KnowledgeItemResponse } from "../../gen/models";
import { useToast } from "../../context/ToastContext";
import { extractListItems } from "./threadUtils";

interface UseCommentEditorProps {
  threadId?: string;
  setComments: React.Dispatch<React.SetStateAction<CommentResponse[]>>;
  activeHighlightedCommentId: string | null;
  setActiveHighlightedCommentId: (id: string | null) => void;
  onCommentDeleted?: (commentId: string) => void;
}

export function useCommentEditor({
  threadId,
  setComments,
  activeHighlightedCommentId,
  setActiveHighlightedCommentId,
  onCommentDeleted,
}: UseCommentEditorProps) {
  const { showToast } = useToast();
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState("");
  const [editingRuleIds, setEditingRuleIds] = useState<string[]>([]);
  const [editingKnowledgeIds, setEditingKnowledgeIds] = useState<string[]>([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const [mentionType, setMentionType] = useState<"rule" | "knowledge" | null>(null);
  const [mentionQuery, setMentionQuery] = useState("");

  const startEdit = (comment: CommentResponse) => {
    setEditingCommentId(comment.id);
    setEditingText(comment.comment_text);
    setEditingRuleIds(comment.rule_ids || []);
    setEditingKnowledgeIds(comment.knowledge_item_ids || []);
    setMentionType(null);
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setMentionType(null);
  };

  const handleEditTextChange = (val: string) => {
    setEditingText(val);
    const ruleMatch = /@rule:([a-zA-Z0-9_\-\s]*)$/.exec(val);
    if (ruleMatch) {
      setMentionType("rule");
      setMentionQuery(ruleMatch[1].trim().toLowerCase());
      return;
    }
    const kMatch = /@knowledge:([a-zA-Z0-9_\-\s]*)$/.exec(val);
    if (kMatch) {
      setMentionType("knowledge");
      setMentionQuery(kMatch[1].trim().toLowerCase());
      return;
    }
    setMentionType(null);
    setMentionQuery("");
  };

  const handleSelectMentionRule = (rule: RuleResponse) => {
    if (!editingRuleIds.includes(rule.id)) {
      setEditingRuleIds((prev) => [...prev, rule.id]);
    }
    setEditingText((prev) => prev.replace(/@rule:[a-zA-Z0-9_\-\s]*$/, ""));
    setMentionType(null);
    setMentionQuery("");
  };

  const handleSelectMentionKnowledge = (k: KnowledgeItemResponse) => {
    if (!editingKnowledgeIds.includes(k.id)) {
      setEditingKnowledgeIds((prev) => [...prev, k.id]);
    }
    setEditingText((prev) => prev.replace(/@knowledge:[a-zA-Z0-9_\-\s]*$/, ""));
    setMentionType(null);
    setMentionQuery("");
  };

  const handleUpdateComment = async (commentId: string) => {
    if (!editingText.trim()) return;
    setSavingEdit(true);
    try {
      const res = await commentsUpdateComment(commentId, {
        comment_text: editingText.trim(),
        rule_ids: editingRuleIds,
        knowledge_item_ids: editingKnowledgeIds,
      });

      const updated = (res as any).data;
      if (updated) {
        setComments((prev) => prev.map((c) => (c.id === commentId ? { ...c, ...updated } : c)));
      } else if (threadId) {
        const commsRes = await commentsListComments({ thread_id: threadId, limit: 200 });
        setComments(extractListItems<CommentResponse>(commsRes));
      }

      setEditingCommentId(null);
    } catch (err: any) {
      showToast("Failed to update comment: " + (err?.message || "Unknown error"), { type: "error" });
    } finally {
      setSavingEdit(false);
    }
  };

  const [deleteModalCommentId, setDeleteModalCommentId] = useState<string | null>(null);
  const [deletingComment, setDeletingComment] = useState(false);

  const handleDeleteComment = (commentId: string) => {
    setDeleteModalCommentId(commentId);
  };

  const handleCancelDelete = () => {
    setDeleteModalCommentId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalCommentId) return;
    const toDeleteId = deleteModalCommentId;
    setDeletingComment(true);
    try {
      await commentsDeleteComment(toDeleteId);
      onCommentDeleted?.(toDeleteId);
      setComments((prev) => prev.filter((c) => c.id !== toDeleteId));
      if (activeHighlightedCommentId === toDeleteId) {
        setActiveHighlightedCommentId(null);
      }
      showToast("Comment deleted", { type: "info" });
      setDeleteModalCommentId(null);
    } catch (err: any) {
      showToast("Failed to delete comment: " + (err?.message || "Unknown error"), { type: "error" });
    } finally {
      setDeletingComment(false);
    }
  };

  const removeEditingRule = (rid: string) => {
    setEditingRuleIds((prev) => prev.filter((id) => id !== rid));
  };

  const removeEditingKnowledge = (kid: string) => {
    setEditingKnowledgeIds((prev) => prev.filter((id) => id !== kid));
  };

  return {
    editingCommentId,
    setEditingCommentId,
    editingText,
    editingRuleIds,
    editingKnowledgeIds,
    savingEdit,
    mentionType,
    mentionQuery,
    deleteModalCommentId,
    deletingComment,
    startEdit,
    cancelEdit,
    handleEditTextChange,
    handleSelectMentionRule,
    handleSelectMentionKnowledge,
    handleUpdateComment,
    handleDeleteComment,
    handleCancelDelete,
    handleConfirmDelete,
    removeEditingRule,
    removeEditingKnowledge,
  };
}
